import { styled, alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

const CalendarStyle = styled('div')(({ theme }) => ({
  width: 'calc(100% + 2px)',
  marginLeft: -1,
  marginBottom: -1,
  '& .fc': {
    '--fc-small-font-size': '0.813rem',
    '--fc-list-event-dot-width': '8px',
    '--fc-border-color': theme.palette.divider,
    '--fc-now-indicator-color': theme.palette.error.main,
    '--fc-page-bg-color': theme.palette.background.default,
    '--fc-neutral-bg-color': theme.palette.background.neutral,
    '--fc-list-event-hover-bg-color': theme.palette.action.hover,
    '--fc-highlight-color': alpha(theme.palette.grey[500], 0.12),
    '--fc-non-business-color': alpha(theme.palette.grey[500], 0.08),
    '--fc-today-bg-color': 'transparent',
  },

  '& .fc .fc-license-message': { display: 'none' },
  '& .fc a': { color: theme.palette.text.primary },

  // Table Head
  '& .fc .fc-col-header ': {
    boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`,
    '& th': { borderColor: 'transparent' },
    '& .fc-col-header-cell-cushion': {
      ...theme.typography.subtitle2,
      paddingTop: 12.5,
      paddingBottom: 12.5,
    },
  },

  // Month base day numbers
  '& .fc .fc-daygrid-day-number': {
    ...theme.typography.body2,
    lineHeight: '26px',
    padding: '4px 8px',
  },
  // Today circle highlight
  '& .fc .fc-day-today .fc-daygrid-day-number': {
    display: 'inline-flex',
    justifyContent: 'center',
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightSemiBold || 600,
    width: 'calc(26px + 8px)',
    position: 'relative',
  },
  '& .fc .fc-day-today .fc-daygrid-day-number::before': {
    zIndex: -1,
    content: '""',
    borderRadius: '50%',
    position: 'absolute',
    backgroundColor: theme.palette.error.main,
    width: 26,
    height: 26,
  },
  // Weekend subtler color
  '& .fc .fc-day-sat, & .fc .fc-day-sun': {
    '&.fc-col-header-cell, & .fc-daygrid-day-top': {
      color: theme.palette.text.secondary,
    },
  },
  '& .fc .fc-day-sat .fc-daygrid-day-frame, & .fc .fc-day-sun .fc-daygrid-day-frame': {
    backgroundColor: alpha(theme.palette.grey[500], 0.08),
  },
  // Other month days
  '& .fc .fc-day-other .fc-daygrid-day-top': {
    opacity: 1,
    color: theme.palette.action.disabled,
  },
  '& .fc .fc-day-other .fc-daygrid-day-number': {
    color: theme.palette.text.disabled,
  },
  // Tint background for other-month cells
  '& .fc .fc-day-other .fc-daygrid-day-frame': {
    backgroundColor: alpha(theme.palette.grey[500], 0.04),
  },

  // Events
  '& .fc .fc-event': {
    borderWidth: 0,
    borderRadius: 6,
    boxShadow: 'none',
    backgroundColor: 'transparent',
  },
  '& .fc .fc-daygrid-event': {
    marginTop: 0,
    marginBottom: 4,
  },
  // Subtle visual for multi-day timed events we mark via class
  '& .fc .fc-daygrid-event.is-multi-range': {
    minHeight: 20,
    borderRadius: 8,
  },
  '& .fc .fc-daygrid-event.is-multi-range .fc-event-main': {
    // keep default border from base rule, just soften background a bit further
    backgroundColor: alpha('#000', 0.01),
    borderColor: alpha('#000', 0.08),
  },
  '& .fc .fc-daygrid-event.is-multi-range.fc-event-start .fc-event-main': {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  '& .fc .fc-daygrid-event.is-multi-range.fc-event-end .fc-event-main': {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
  '& .fc .fc-daygrid-event.fc-event-end, & .fc .fc-daygrid-event.fc-event-start': {
    marginLeft: 4,
    marginRight: 4,
  },
  '& .fc .fc-event .fc-event-main': {
    padding: '2px 6px',
    borderRadius: 6,
    // Soften default border to reduce visual noise
    border: `solid 1px ${alpha('#000', 0.1)}`,
    transition: theme.transitions.create(['background-color', 'border-color']),
    // Slightly lighter base fill so the colored tint from JS remains subtle
    backgroundColor: alpha(theme.palette.common.white, 1 - 0.18),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 1 - 0.28),
    },
  },
  '& .fc .fc-event .fc-event-main-frame': {
    fontSize: 13,
    lineHeight: '20px',
    filter: 'brightness(0.48)',
  },
  '& .fc .fc-daygrid-event .fc-event-title': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  '& .fc .fc-event .fc-event-time': {
    padding: 0,
    overflow: 'unset',
    fontWeight: theme.typography.fontWeightBold,
  },

  // Popover
  '& .fc .fc-popover': {
    border: 0,
    overflow: 'hidden',
    boxShadow: theme.customShadows?.z20 || '0 8px 20px rgba(0,0,0,0.12)',
    borderRadius: Number(theme.shape.borderRadius) * 1.5,
    backgroundColor: theme.palette.background.paper,
  },
  '& .fc .fc-popover-header': {
    ...theme.typography.subtitle2,
    padding: theme.spacing(1),
    backgroundColor: alpha(theme.palette.grey[500], 0.12),
    borderBottom: `solid 1px ${theme.palette.divider}`,
  },
  '& .fc .fc-popover-close': {
    opacity: 0.48,
    transition: theme.transitions.create('opacity'),
    '&:hover': { opacity: 0.8 },
  },
  '& .fc .fc-more-popover .fc-popover-body': {
    padding: theme.spacing(0.5),
  },
  '& .fc .fc-popover-body': {
    '& .fc-daygrid-event.fc-event-start, & .fc-daygrid-event.fc-event-end': {
      margin: '2px 0',
    },
  },

  // Week & Day View
  '& .fc .fc-timegrid-axis-cushion': {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
  },
  '& .fc .fc-timegrid-slot-label-cushion': {
    ...theme.typography.body2,
  },

  // List (Agenda) View
  '& .fc-direction-ltr .fc-list-day-text, .fc-direction-rtl .fc-list-day-side-text, .fc-direction-ltr .fc-list-day-side-text, .fc-direction-rtl .fc-list-day-text':
    {
      ...theme.typography.subtitle2,
    },
  '& .fc .fc-list-event': {
    ...theme.typography.body2,
    '& .fc-list-event-time': {
      color: theme.palette.text.secondary,
    },
  },
  '& .fc .fc-list-table': {
    '& th, td': {
      borderColor: 'transparent',
    },
  },

  // More links
  '& .fc .fc-daygrid-more-link': {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium || 500,
    padding: theme.spacing(0, 1),
    transition: theme.transitions.create(['color']),
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
    },
  },
}));

export default CalendarStyle;
